if (!customElements.get('returns-page')) {
  customElements.define('returns-page', class ReturnsPage extends HTMLElement {
    connectedCallback() {
      const initialize = () => {
        if (this.initialized) return;
        this.initialized = true;
        this.tabs = [...this.querySelectorAll('[data-returns-route]')];
        this.panels = [...this.querySelectorAll('[data-returns-panel]')];
        this.form = this.querySelector('[data-returns-native-form]');
        this.returnTypeInput = this.querySelector('[data-returns-type-input]');
        this.returnTypeLabel = this.querySelector('[data-returns-type-label]');
        this.formHeading = this.querySelector('[data-returns-form-heading]');
        this.formDescription = this.querySelector('[data-returns-form-description]');
        this.formNote = this.querySelector('[data-returns-form-note]');
        this.formStatus = this.querySelector('[data-returns-form-status]');
        this.submitButton = this.form?.querySelector('[type="submit"]');
        this.orderSelector = this.querySelector('[data-returns-order-selector]');
        this.selectorStatus = this.querySelector('[data-returns-selector-status]');
        this.contextField = this.querySelector('[data-returns-context-field]');
        this.contextSelect = this.querySelector('[data-returns-context-select]');
        this.ordersContainer = this.querySelector('[data-returns-orders]');
        this.loadMoreButton = this.querySelector('[data-returns-load-more]');
        this.selectedOrderPanel = this.querySelector('[data-returns-selected-order]');
        this.selectedOrderTitle = this.querySelector('[data-returns-selected-order-title]');
        this.lineItemsContainer = this.querySelector('[data-returns-line-items]');
        this.orderField = this.querySelector('[data-returns-order-field]');
        this.orderInput = this.form?.elements.order;
        this.productsField = this.querySelector('[data-returns-products-field]');
        this.detailsGroup = this.querySelector('[data-returns-details-group]');
        this.productsInput = this.form?.elements.products;
        this.reasonInput = this.form?.elements.reason;
        this.reasonLabel = this.querySelector('[data-returns-reason-label]');
        this.reasonHelp = this.querySelector('[data-returns-reason-help]');
        this.shopifyOrderIdInput = this.querySelector('[data-returns-shopify-order-id]');
        this.purchasingContextInput = this.querySelector('[data-returns-purchasing-context]');
        this.selectedItemsInput = this.querySelector('[data-returns-selected-items]');
        this.orderNotesInput = this.querySelector('[data-returns-order-notes]');
        this.manualToggle = this.querySelector('[data-returns-manual-toggle]');
        this.farPanel = this.querySelector('[data-returns-far]');
        this.farHeading = this.querySelector('[data-returns-far-heading]');
        this.farSummary = this.querySelector('[data-returns-far-summary]');
        this.farStatus = this.querySelector('[data-returns-far-status]');
        this.farMessage = this.querySelector('[data-returns-far-message]');
        this.farDetails = this.querySelector('[data-returns-far-details]');
        this.farSignerRow = this.querySelector('[data-returns-far-signer-row]');
        this.farSigner = this.querySelector('[data-returns-far-signer]');
        this.farDateRow = this.querySelector('[data-returns-far-date-row]');
        this.farDate = this.querySelector('[data-returns-far-date]');
        this.farVersionRow = this.querySelector('[data-returns-far-version-row]');
        this.farVersion = this.querySelector('[data-returns-far-version]');
        this.farChoice = this.querySelector('[data-returns-far-choice]');
        this.farRequest = this.querySelector('[data-returns-far-request]');
        this.farChoiceDescription = this.querySelector('[data-returns-far-choice-description]');
        this.farLink = this.querySelector('[data-returns-far-link]');
        this.awaitingFarRefresh = false;
        this.farRefreshAttempts = 0;
        this.farRefreshTimer = null;
        this.customerAuthenticated = this.dataset.customerAuthenticated === 'true';
        this.manualMode = !this.customerAuthenticated;
        this.orders = [];
        this.contexts = [];
        this.pageInfo = null;

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.selectRoute(tab.dataset.returnsRoute));
          tab.addEventListener('keydown', (event) => this.handleTabKeydown(event));
        });
        this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
        this.contextSelect?.addEventListener('change', () => this.loadOrders(true));
        this.loadMoreButton?.addEventListener('click', () => this.loadOrders(false));
        this.querySelector('[data-returns-change-order]')?.addEventListener('click', () => this.clearSelectedOrder());
        this.manualToggle?.addEventListener('click', () => this.enableManualMode());
        this.farLink?.addEventListener('click', () => {
          const context = this.currentContext();
          if (context?.far_agreement?.eligible !== true) {
            window.clearTimeout(this.farRefreshTimer);
            this.farRefreshAttempts = 0;
            this.awaitingFarRefresh = true;
          }
        });
        window.addEventListener('focus', () => this.refreshFarAgreement());
        window.addEventListener('storage', (event) => {
          if (event.key !== 'nellys:far-agreement-updated' || !event.newValue) return;

          try {
            const update = JSON.parse(event.newValue);
            const context = this.currentContext();
            if (context?.company_id && String(context.company_id) === String(update.company_id)) {
              window.clearTimeout(this.farRefreshTimer);
              this.farRefreshAttempts = 0;
              this.awaitingFarRefresh = true;
              this.refreshFarAgreement();
            }
          } catch (error) {
            console.warn('Could not read the FAR Agreement update notification.', error);
          }
        });
        document.addEventListener('visibilitychange', () => {
          if (!document.hidden) this.refreshFarAgreement();
        });
        this.orderInput?.addEventListener('input', () => {
          if (this.manualMode) this.updateFarPanel();
        });

        const requestedType = new URLSearchParams(window.location.search).get('type');
        const initialRoute = requestedType === 'warranty' || window.location.hash === '#warranty-return'
          ? 'warranty'
          : 'standard';
        this.selectRoute(initialRoute, false);
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize, { once: true });
      } else {
        initialize();
      }
    }

    handleTabKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const current = this.tabs.indexOf(event.currentTarget);
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const next = (current + direction + this.tabs.length) % this.tabs.length;
      this.selectRoute(this.tabs[next].dataset.returnsRoute);
      this.tabs[next].focus();
    }

    selectRoute(route, updateHash = true) {
      const selectedTab = this.tabs.find((tab) => tab.dataset.returnsRoute === route) || this.tabs[0];
      if (!selectedTab) return;
      this.tabs.forEach((tab) => {
        const selected = tab === selectedTab;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach((panel) => { panel.hidden = panel.dataset.returnsPanel !== selectedTab.dataset.returnsRoute; });
      const isWarranty = selectedTab.dataset.returnsRoute === 'warranty';
      this.formHeading.textContent = isWarranty ? 'Start a Warranty Return' : 'Start a 60-Day Return';
      this.formDescription.textContent = isWarranty
        ? 'Complete the form below and our team will review your warranty request.'
        : 'Complete the form below and our team will review your 60-day return request.';
      if (this.formNote) this.formNote.hidden = isWarranty;
      if (this.returnTypeInput) this.returnTypeInput.value = selectedTab.dataset.returnValue;
      if (this.returnTypeLabel) this.returnTypeLabel.textContent = selectedTab.dataset.returnValue;
      this.updateFarPanel();
      if (updateHash) {
        const hash = isWarranty ? '#warranty-return' : '#60-day-return';
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`);
      }

      this.clearSelectedOrder(false);
      if (this.customerAuthenticated) this.loadOrders(true);
    }

    async loadOrders(reset = true) {
      if (!this.customerAuthenticated || !this.dataset.ordersUrl) return;
      if (reset) {
        this.clearSelectedOrder();
        this.orders = [];
        this.pageInfo = null;
        if (this.farPanel) this.farPanel.hidden = true;
        if (this.farRequest) this.farRequest.checked = false;
      }
      const requestId = `${Date.now()}-${Math.random()}`;
      this.ordersRequestId = requestId;
      this.orderSelector.hidden = false;
      if (reset && this.ordersContainer) this.ordersContainer.hidden = true;
      if (reset) this.setSelectorStatus('Loading orders...', 'loading');
      if (this.loadMoreButton) {
        if (reset) this.loadMoreButton.hidden = true;
        else this.setLoadMoreState(true);
      }

      const url = new URL(this.dataset.ordersUrl, window.location.origin);
      url.searchParams.set('return_type', this.returnTypeInput.value);
      if (this.contextSelect?.value) url.searchParams.set('context', this.contextSelect.value);
      if (!reset && this.pageInfo?.end_cursor) url.searchParams.set('after', this.pageInfo.end_cursor);

      try {
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.message || 'We could not load your orders.');
        if (this.ordersRequestId !== requestId) return;

        this.setEnhancedMode(!this.manualMode);
        this.updateContexts(result.contexts || [], result.selected_context);
        this.orders = reset ? (result.orders || []) : [...this.orders, ...(result.orders || [])];
        this.pageInfo = result.page_info || null;
        this.renderOrders();
      } catch (error) {
        if (this.ordersRequestId !== requestId) return;
        if (reset && this.ordersContainer) this.ordersContainer.hidden = true;
        this.setLoadMoreState(false);
        this.setEnhancedMode(false);
        this.setSelectorStatus(`${error.message || 'We could not load your orders.'} Enter your order manually below.`, 'error');
      }
    }

    updateContexts(contexts, selectedContext) {
      if (!this.contextSelect) return;
      this.contexts = contexts;
      const currentValue = this.contextSelect.value;
      this.contextSelect.replaceChildren();
      this.contexts.forEach((context) => {
        const option = document.createElement('option');
        option.value = context.key;
        option.textContent = context.label;
        this.contextSelect.append(option);
      });
      const desiredValue = currentValue || selectedContext || 'personal';
      if ([...this.contextSelect.options].some((option) => option.value === desiredValue)) {
        this.contextSelect.value = desiredValue;
      }
      this.contextField.hidden = this.contexts.length < 2;
      if (this.purchasingContextInput) this.purchasingContextInput.value = this.contextSelect.value || 'personal';
      this.updateFarPanel();
    }

    updateFarPanel() {
      if (!this.farPanel) return;
      const isWarranty = this.returnTypeInput?.value === 'Warranty Return';
      const context = this.currentContext();
      const isCompany = context?.type === 'company';
      const hasEstablishedOrder = this.manualMode
        ? Boolean(this.orderInput?.value.trim())
        : Boolean(this.selectedOrder);
      this.farPanel.hidden = !isWarranty || !isCompany || !hasEstablishedOrder;

      if (this.farPanel.hidden) {
        if (this.farRequest) this.farRequest.checked = false;
        return;
      }

      const agreement = context.far_agreement || {};
      const status = agreement.status;
      this.farPanel.dataset.status = status || 'unsigned';
      this.farChoice.hidden = true;
      this.farChoice.classList.remove('returns__far-choice--disabled');
      this.farRequest.disabled = false;
      this.farHeading.textContent = 'Request a Free Advance Replacement';
      this.farSummary.textContent = 'Get an approved replacement shipped before returning the original product, helping reduce downtime while your warranty claim is reviewed.';
      this.farSummary.hidden = false;
      this.farMessage.hidden = false;
      if (this.farChoiceDescription) {
        this.farChoiceDescription.textContent = 'Our team will verify warranty coverage, payment eligibility, and product availability.';
      }
      this.farLink.hidden = true;
      this.farLink.classList.remove('returns__far-link--primary');
      this.farDetails.hidden = true;
      this.farSignerRow.hidden = true;
      this.farDateRow.hidden = true;
      this.farVersionRow.hidden = true;
      this.farStatus.textContent = '';
      this.farLink.href = `/apps/nellys/agreements/far?company=${encodeURIComponent(context.company_id)}`;

      if (agreement.eligible) {
        this.farStatus.textContent = 'Active';
        this.farMessage.textContent = 'An active Free Advance Replacement Agreement is on file for your company.';
        this.updateFarDetails(agreement);
        this.farChoice.hidden = false;
        if (agreement.pdf_url) {
          this.farLink.href = agreement.pdf_url;
          this.farLink.textContent = 'View signed agreement';
          this.farLink.hidden = false;
        }
        return;
      }

      if (status === 'revoked') {
        this.farStatus.textContent = 'Unavailable';
        this.farMessage.textContent = 'Free Advance Replacement access is currently unavailable for your company. Contact our team for assistance.';
        this.farChoice.hidden = false;
        this.farChoice.classList.add('returns__far-choice--disabled');
        this.farRequest.disabled = true;
        this.farRequest.checked = false;
        if (this.farChoiceDescription) {
          this.farChoiceDescription.textContent = 'Free Advance Replacement access is currently unavailable for your company.';
        }
        return;
      }

      const requiresUpdate = agreement.requires_update === true;
      this.farStatus.textContent = requiresUpdate ? 'Update required' : 'Agreement required';
      this.farPanel.dataset.status = requiresUpdate ? 'update-required' : 'agreement-required';
      this.farMessage.textContent = 'A Free Advance Replacement Agreement must be signed before an advance replacement can be requested.';

      if (requiresUpdate) {
        this.farHeading.textContent = 'Updated agreement required';
        this.farSummary.textContent = 'A newer version of the Free Advance Replacement Agreement is available. Review and sign the updated agreement to continue requesting advance replacements.';
        this.farMessage.hidden = true;
        this.farChoice.hidden = false;
        this.farChoice.classList.add('returns__far-choice--disabled');
        this.farRequest.disabled = true;
        if (this.farChoiceDescription) {
          this.farChoiceDescription.textContent = 'Sign the updated Free Advance Replacement Agreement to enable this option.';
        }
        this.farLink.classList.add('returns__far-link--primary');
      } else {
        this.farChoice.hidden = false;
        this.farChoice.classList.add('returns__far-choice--disabled');
        this.farRequest.disabled = true;
        if (this.farChoiceDescription) {
          this.farChoiceDescription.textContent = 'Sign the Free Advance Replacement Agreement to enable this option.';
        }
      }
      this.farLink.textContent = requiresUpdate ? 'Review and sign updated agreement' : 'Review and sign agreement';
      this.farLink.hidden = false;
      if (this.farRequest) this.farRequest.checked = false;
    }

    currentContext() {
      return this.contexts?.find((item) => item.key === this.contextSelect?.value);
    }

    async refreshFarAgreement() {
      if (!this.awaitingFarRefresh || !this.dataset.farStatusUrl) return;
      const context = this.currentContext();
      if (!context?.company_id || context.type !== 'company') return;
      this.awaitingFarRefresh = false;

      const url = new URL(this.dataset.farStatusUrl, window.location.origin);
      url.searchParams.set('company_id', context.company_id);
      url.searchParams.set('_', Date.now().toString());

      try {
        const response = await fetch(url, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.message || 'Could not refresh FAR status.');
        context.far_agreement = result.far_agreement;
        this.updateFarPanel();

        const agreement = result.far_agreement || {};
        if (agreement.eligible !== true && agreement.status !== 'revoked' && this.farRefreshAttempts < 8) {
          this.farRefreshAttempts += 1;
          this.awaitingFarRefresh = true;
          window.clearTimeout(this.farRefreshTimer);
          this.farRefreshTimer = window.setTimeout(
            () => this.refreshFarAgreement(),
            this.farRefreshAttempts * 1500,
          );
        } else {
          this.farRefreshAttempts = 0;
        }
      } catch (error) {
        this.awaitingFarRefresh = true;
        console.warn('FAR Agreement status refresh failed.', error);
      }
    }

    updateFarDetails(agreement) {
      const signer = agreement.signer_name?.trim();
      const signedAt = this.formatAgreementDate(agreement.signed_at);
      const version = agreement.agreement_version?.trim();

      if (signer) {
        this.farSigner.textContent = signer;
        this.farSignerRow.hidden = false;
      }
      if (signedAt) {
        this.farDate.textContent = signedAt;
        this.farDateRow.hidden = false;
      }
      if (version) {
        this.farVersion.textContent = version;
        this.farVersionRow.hidden = false;
      }

      this.farDetails.hidden = !signer && !signedAt && !version;
    }

    formatAgreementDate(value) {
      if (!value) return '';
      const date = new Date(value.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`);
      if (Number.isNaN(date.getTime())) return '';

      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
    }

    renderOrders() {
      this.ordersContainer.replaceChildren();
      this.setLoadMoreState(false);
      if (!this.orders.length) {
        this.ordersContainer.hidden = true;
        this.setSelectorStatus('No orders were found for this purchasing account and return type.', 'empty');
        if (this.loadMoreButton) this.loadMoreButton.hidden = !this.pageInfo?.has_next_page;
        return;
      }

      this.ordersContainer.hidden = false;
      this.setSelectorStatus('');
      this.orders.forEach((order) => this.ordersContainer.append(this.createOrderCard(order)));
      if (this.loadMoreButton) this.loadMoreButton.hidden = !this.pageInfo?.has_next_page;
    }

    setLoadMoreState(loading) {
      if (!this.loadMoreButton) return;
      this.loadMoreButton.disabled = loading;
      this.loadMoreButton.classList.toggle('is-loading', loading);
      this.loadMoreButton.textContent = loading ? 'Loading more orders...' : 'Load more orders';
    }

    createOrderCard(order) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'returns__order-card';
      button.dataset.orderId = order.legacy_id;
      button.setAttribute('aria-expanded', 'false');
      const eligible = order.return_status?.eligible !== false;
      button.classList.toggle('is-unavailable', !eligible);
      button.setAttribute('aria-disabled', String(!eligible));
      if (eligible) button.addEventListener('click', () => this.selectOrder(order));

      const main = document.createElement('span');
      main.className = 'returns__order-card-main';
      const title = document.createElement('strong');
      title.textContent = order.name;
      const meta = document.createElement('small');
      meta.textContent = `${this.formatDate(order.processed_at)} · ${order.item_count} ${order.item_count === 1 ? 'item' : 'items'} · ${this.formatMoney(order.total)}`;
      main.append(title);
      if (order.po_number) {
        const poNumber = document.createElement('small');
        poNumber.className = 'returns__order-po';
        poNumber.textContent = `PO: ${order.po_number}`;
        main.append(poNumber);
      }
      const summary = document.createElement('span');
      summary.className = 'returns__order-summary';
      const date = document.createElement('span');
      date.className = 'returns__order-date';
      date.textContent = this.formatDate(order.processed_at);
      const itemCount = document.createElement('span');
      itemCount.textContent = `${order.item_count} ${order.item_count === 1 ? 'item' : 'items'}`;
      const total = document.createElement('span');
      total.textContent = this.formatMoney(order.total);
      main.append(date);
      summary.append(itemCount, total);
      let statusBadge = null;
      if (order.return_status?.label) {
        statusBadge = this.createStatusBadge(order.return_status.label, eligible ? 'eligible' : 'unavailable');
        statusBadge.classList.add('returns__order-status');
      }

      const previews = document.createElement('span');
      previews.className = 'returns__order-previews';
      const previewProducts = order.product_preview || [];
      const firstPreview = previewProducts[0];
      if (firstPreview?.image?.url) {
        const image = document.createElement('img');
        image.src = firstPreview.image.url;
        image.alt = '';
        image.loading = 'lazy';
        previews.append(image);
      } else {
        previews.append(this.createImagePlaceholder('returns__order-preview-placeholder'));
      }
      const additionalItems = Math.max(Number(order.item_count || 0) - 1, previewProducts.length - 1, 0);
      if (additionalItems > 0) {
        const count = document.createElement('span');
        count.className = 'returns__order-preview-count';
        count.textContent = `+${additionalItems}`;
        count.setAttribute('aria-label', `${additionalItems} additional ${additionalItems === 1 ? 'item' : 'items'}`);
        previews.append(count);
      }

      const action = document.createElement('span');
      action.className = 'returns__order-card-action';
      action.textContent = eligible ? 'Select' : 'Unavailable';
      button.append(main, summary, previews);
      if (statusBadge) button.append(statusBadge);
      button.append(action);
      return button;
    }

    async selectOrder(order) {
      this.clearSelectedOrder();
      const orderCard = this.ordersContainer.querySelector(`[data-order-id="${CSS.escape(String(order.legacy_id))}"]`);
      this.setOrderLoading(orderCard, true);
      this.setSelectorStatus('');
      const url = new URL(`${this.dataset.ordersUrl}/${order.legacy_id}`, window.location.origin);
      url.searchParams.set('return_type', this.returnTypeInput.value);
      url.searchParams.set('context', this.contextSelect?.value || 'personal');

      try {
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.message || 'We could not load that order.');
        this.manualMode = false;
        if (this.manualToggle) this.manualToggle.hidden = false;
        this.setEnhancedMode(true);
        this.renderSelectedOrder(result.order);
        this.setSelectorStatus('');
      } catch (error) {
        this.setSelectorStatus(error.message || 'We could not load that order.', 'error');
      } finally {
        this.setOrderLoading(orderCard, false);
      }
    }

    setOrderLoading(card, loading) {
      if (!card) return;
      card.classList.toggle('is-loading', loading);
      card.disabled = loading;
      const action = card.querySelector('.returns__order-card-action');
      if (!action) return;

      if (loading) {
        action.replaceChildren();
        const spinner = document.createElement('span');
        spinner.className = 'returns__spinner';
        spinner.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span');
        label.textContent = 'Loading...';
        action.append(spinner, label);
        return;
      }

      action.textContent = card.classList.contains('is-selected') ? 'Selected' : 'Select';
    }

    renderSelectedOrder(order) {
      this.selectedOrder = order;
      const selectedCard = this.ordersContainer.querySelector(`[data-order-id="${CSS.escape(String(order.legacy_id))}"]`);
      this.ordersContainer.querySelectorAll('.returns__order-card').forEach((card) => {
        const selected = card === selectedCard;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-expanded', String(selected));
        const action = card.querySelector('.returns__order-card-action');
        if (action) action.textContent = selected
          ? 'Selected'
          : (card.getAttribute('aria-disabled') === 'true' ? 'Unavailable' : 'Select');
      });
      if (this.loadMoreButton) this.loadMoreButton.hidden = true;
      this.selectedOrderPanel.hidden = false;
      if (selectedCard) selectedCard.after(this.selectedOrderPanel);
      this.selectedOrderTitle.textContent = `Choose products from order ${order.name}`;
      this.lineItemsContainer.replaceChildren();
      order.line_items.forEach((item) => this.lineItemsContainer.append(this.createLineItem(item)));
      this.orderInput.value = order.name;
      this.shopifyOrderIdInput.value = order.legacy_id;
      this.purchasingContextInput.value = this.contextSelect?.value || 'personal';
      this.updateFarPanel();
    }

    createLineItem(item) {
      const maximum = this.returnTypeInput.value === '60-Day Return'
        ? item.refundable_quantity
        : item.current_quantity;
      const row = document.createElement('div');
      row.className = 'returns__line-item';
      row.dataset.lineItemId = item.id;
      row.dataset.lineItemName = item.name;
      row.dataset.lineItemSku = item.sku || '';

      const choice = document.createElement('label');
      choice.className = 'returns__line-item-choice';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.disabled = maximum < 1;
      checkbox.dataset.lineItemCheckbox = '';
      const image = item.image?.url
        ? document.createElement('img')
        : this.createImagePlaceholder('returns__line-item-placeholder');
      if (item.image?.url) {
        image.src = item.image.url;
        image.alt = item.image.alt || '';
        image.loading = 'lazy';
      }
      const copy = document.createElement('span');
      const name = document.createElement('strong');
      name.textContent = item.name;
      const sku = document.createElement('small');
      sku.textContent = item.sku ? `SKU: ${item.sku}` : 'SKU unavailable';
      const purchasedMobile = document.createElement('small');
      purchasedMobile.className = 'returns__line-item-purchased-mobile';
      purchasedMobile.textContent = `Purchased: ${item.quantity}`;
      const status = this.createStatusBadge(
        maximum > 0
          ? (this.returnTypeInput.value === '60-Day Return' ? 'Available for return' : 'Available for warranty review')
          : 'No eligible quantity remaining',
        maximum > 0 ? 'eligible' : 'unavailable',
      );
      copy.append(name, sku, purchasedMobile, status);
      choice.append(checkbox, image, copy);

      const purchased = document.createElement('span');
      purchased.className = 'returns__line-item-purchased';
      purchased.textContent = String(item.quantity);

      const controls = document.createElement('div');
      controls.className = 'returns__line-item-controls';
      controls.hidden = true;
      const quantity = this.createQuantityField(maximum);
      const reason = this.createReasonField();
      const details = this.createDetailsField();
      controls.append(quantity.wrapper, reason.wrapper, details.wrapper);
      checkbox.addEventListener('change', () => {
        controls.hidden = !checkbox.checked;
        reason.select.required = checkbox.checked;
      });

      row.append(choice, purchased, controls);
      if (maximum < 1) {
        const unavailable = document.createElement('small');
        unavailable.className = 'returns__line-item-unavailable';
        unavailable.textContent = 'No eligible quantity remains for this item.';
        row.append(unavailable);
      }
      return row;
    }

    createQuantityField(maximum) {
      const wrapper = document.createElement('label');
      const caption = document.createElement('span');
      caption.className = 'returns__control-label';
      caption.textContent = 'Quantity';
      const select = document.createElement('select');
      select.dataset.lineItemQuantity = '';
      for (let quantity = 1; quantity <= maximum; quantity += 1) {
        const option = document.createElement('option');
        option.value = String(quantity);
        option.textContent = String(quantity);
        select.append(option);
      }
      wrapper.append(caption, select);
      return { wrapper, select };
    }

    createReasonField() {
      const wrapper = document.createElement('label');
      const caption = document.createElement('span');
      caption.className = 'returns__control-label';
      caption.textContent = 'Reason ';
      const requiredMarker = document.createElement('span');
      requiredMarker.className = 'returns__required-marker';
      requiredMarker.setAttribute('aria-hidden', 'true');
      requiredMarker.textContent = '*';
      caption.append(requiredMarker);
      const select = document.createElement('select');
      select.dataset.lineItemReason = '';
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select a reason';
      select.append(placeholder);
      this.reasonOptions().forEach(([value, label]) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        select.append(option);
      });
      wrapper.append(caption, select);
      return { wrapper, select };
    }

    createDetailsField() {
      const wrapper = document.createElement('label');
      wrapper.className = 'returns__line-item-details';
      const caption = document.createElement('span');
      caption.className = 'returns__control-label';
      caption.textContent = 'Details (optional)';
      const textarea = document.createElement('textarea');
      textarea.rows = 3;
      textarea.maxLength = 1000;
      textarea.placeholder = this.returnTypeInput.value === 'Warranty Return'
        ? 'Describe the problem, when it occurs, and any troubleshooting completed'
        : 'Add details about the product condition or reason for returning it';
      textarea.dataset.lineItemDetails = '';
      wrapper.append(caption, textarea);
      return { wrapper, textarea };
    }

    reasonOptions() {
      return this.returnTypeInput.value === 'Warranty Return'
        ? [
          ['stopped_working', 'Product stopped working'],
          ['intermittent_operation', 'Intermittent operation'],
          ['power_issue', 'Power issue'],
          ['connectivity_issue', 'Connectivity or network issue'],
          ['image_quality_issue', 'Video or image-quality issue'],
          ['physical_defect', 'Physical defect'],
          ['missing_components', 'Missing components'],
          ['other', 'Other'],
        ]
        : [
          ['no_longer_needed', 'No longer needed'],
          ['ordered_wrong_product', 'Ordered the wrong product'],
          ['incompatible', 'Not compatible with my system'],
          ['not_as_expected', 'Product was not what I expected'],
          ['arrived_damaged', 'Arrived damaged'],
          ['missing_parts', 'Missing parts or accessories'],
          ['other', 'Other'],
        ];
    }

    clearSelectedOrder(clearFields = true) {
      this.selectedOrder = null;
      if (this.selectedOrderPanel) {
        this.selectedOrderPanel.hidden = true;
        if (this.loadMoreButton) this.loadMoreButton.after(this.selectedOrderPanel);
      }
      if (this.ordersContainer) this.ordersContainer.hidden = false;
      this.ordersContainer?.querySelectorAll('.returns__order-card').forEach((card) => {
        card.classList.remove('is-selected');
        card.setAttribute('aria-expanded', 'false');
        const action = card.querySelector('.returns__order-card-action');
        if (action) action.textContent = card.getAttribute('aria-disabled') === 'true' ? 'Unavailable' : 'Select';
      });
      if (this.lineItemsContainer) this.lineItemsContainer.replaceChildren();
      if (clearFields) {
        if (this.orderInput) this.orderInput.value = '';
        if (this.shopifyOrderIdInput) this.shopifyOrderIdInput.value = '';
        if (this.selectedItemsInput) this.selectedItemsInput.value = '';
        if (this.orderNotesInput) this.orderNotesInput.value = '';
      }
      if (this.loadMoreButton) this.loadMoreButton.hidden = !this.pageInfo?.has_next_page;
      this.updateFarPanel();
    }

    createStatusBadge(label, status) {
      const badge = document.createElement('span');
      badge.className = `returns__status-badge is-${status}`;
      badge.textContent = label;
      return badge;
    }

    createImagePlaceholder(className) {
      const placeholder = document.createElement('span');
      placeholder.className = className;
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M7 7.5h1.2l.7-1.4h6.2l.7 1.4H17A2.5 2.5 0 0 1 19.5 10v6A2.5 2.5 0 0 1 17 18.5H7A2.5 2.5 0 0 1 4.5 16v-6A2.5 2.5 0 0 1 7 7.5Zm5 2.2a3.3 3.3 0 1 0 0 6.6 3.3 3.3 0 0 0 0-6.6Zm0 1.7a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2Z"/></svg>';
      return placeholder;
    }

    enableManualMode() {
      this.manualMode = true;
      this.clearSelectedOrder();
      this.orderSelector.hidden = false;
      this.setSelectorStatus('');
      this.setEnhancedMode(false);
      this.updateFarPanel();

      window.requestAnimationFrame(() => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.orderField?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        window.setTimeout(() => this.orderInput?.focus({ preventScroll: true }), reduceMotion ? 0 : 350);
      });
    }

    setEnhancedMode(enabled) {
      if (!this.orderField || !this.productsField) return;
      if (this.detailsGroup) this.detailsGroup.hidden = enabled;
      this.orderField.hidden = enabled;
      this.productsField.hidden = enabled;
      this.orderInput.required = !enabled;
      this.productsInput.required = !enabled;
      this.reasonInput.required = !enabled;
      if (this.reasonLabel) {
        this.reasonLabel.textContent = enabled ? 'Additional notes (optional)' : "What's the reason for your return? *";
      }
      if (this.reasonHelp) {
        this.reasonHelp.textContent = enabled
          ? 'Use this for information that applies to the entire request.'
          : 'For warranty requests, describe the issue and any troubleshooting already completed.';
      }
    }

    selectedLineItems() {
      return [...this.querySelectorAll('.returns__line-item')]
        .filter((row) => row.querySelector('[data-line-item-checkbox]')?.checked)
        .map((row) => {
          const reasonSelect = row.querySelector('[data-line-item-reason]');
          return {
            line_item_id: row.dataset.lineItemId,
            name: row.dataset.lineItemName,
            sku: row.dataset.lineItemSku || null,
            quantity: Number(row.querySelector('[data-line-item-quantity]')?.value || 1),
            reason_code: reasonSelect?.value || '',
            reason_label: reasonSelect?.selectedOptions[0]?.textContent || '',
            details: row.querySelector('[data-line-item-details]')?.value.trim() || null,
          };
        });
    }

    async handleSubmit(event) {
      event.preventDefault();
      let selectedItems = [];
      if (this.customerAuthenticated && !this.manualMode && this.orderSelector && !this.orderSelector.hidden) {
        selectedItems = this.selectedLineItems();
        if (!this.selectedOrder || !selectedItems.length) {
          this.setFormState('error', 'Select an order and at least one product before submitting.');
          return;
        }
      }
      if (!this.form?.reportValidity()) return;

      const selectedRoute = this.tabs.find((tab) => tab.classList.contains('is-active'))?.dataset.returnsRoute || 'standard';
      const formData = new FormData(this.form);
      formData.set('submission_id', this.createSubmissionId());
      if (selectedItems.length) {
        const orderNotes = this.orderNotesInput?.value.trim() || '';
        formData.set('selected_line_items', JSON.stringify(selectedItems));
        formData.set('shopify_order_id', this.selectedOrder.legacy_id);
        formData.set('purchasing_context', this.contextSelect?.value || 'personal');
        formData.set('order', this.selectedOrder.name);
        formData.set('products', selectedItems.map((item) => `${item.quantity} x ${item.name}${item.sku ? ` (${item.sku})` : ''}`).join('\n'));
        formData.set('additional_notes', orderNotes);
        const itemReasons = selectedItems.map((item) => `${item.name}: ${item.reason_label}${item.details ? ` - ${item.details}` : ''}`);
        if (orderNotes) itemReasons.push(`Additional notes: ${orderNotes}`);
        formData.set('reason', itemReasons.join('\n'));
      }
      this.setFormState('submitting', 'Submitting your return request...');

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          const validationMessage = result.errors ? Object.values(result.errors).flat()[0] : null;
          throw new Error(validationMessage || result.message || 'We could not submit your request. Please try again.');
        }

        this.form.reset();
        this.manualMode = !this.customerAuthenticated;
        this.selectRoute(selectedRoute, false);
        this.setFormState('success', result.message || 'Your return request has been submitted.');
      } catch (error) {
        this.setFormState('error', error.message || 'We could not submit your request. Please try again.');
      }
    }

    formatDate(value) {
      return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(value));
    }

    formatMoney(money) {
      if (!money?.amount || !money?.currency_code) return '';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: money.currency_code }).format(Number(money.amount));
    }

    setSelectorStatus(message, state = '') {
      if (!this.selectorStatus) return;
      this.selectorStatus.textContent = message;
      this.selectorStatus.hidden = !message;
      this.selectorStatus.classList.toggle('is-loading', state === 'loading');
      this.selectorStatus.classList.toggle('is-empty', state === 'empty');
      this.selectorStatus.classList.toggle('is-error', state === 'error');
    }

    createSubmissionId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
        const random = Math.random() * 16 | 0;
        const value = character === 'x' ? random : (random & 0x3 | 0x8);
        return value.toString(16);
      });
    }

    setFormState(state, message) {
      if (this.submitButton) {
        const isSubmitting = state === 'submitting';
        const submitLabel = this.submitButton.querySelector('[data-returns-submit-label]');
        this.submitButton.disabled = isSubmitting;
        this.submitButton.classList.toggle('is-loading', isSubmitting);
        this.submitButton.setAttribute('aria-busy', String(isSubmitting));
        if (submitLabel) submitLabel.textContent = isSubmitting ? 'Submitting...' : 'Submit return request';
      }
      if (!this.formStatus) return;
      this.formStatus.hidden = false;
      this.formStatus.textContent = message;
      this.formStatus.classList.toggle('is-success', state === 'success');
      this.formStatus.classList.toggle('is-error', state === 'error');
      this.formStatus.setAttribute('role', state === 'error' ? 'alert' : 'status');
    }
  });
}
